import { zodResolver } from '@hookform/resolvers/zod';
import { useIsMutating, useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';
import { ACTION_LIMITS } from '../ApiAction/useHttpRequestActionFormV2.hook';
import { remoteMcpServerLabel } from './remoteMcpAnalytics';
import queryClient from '@/Config/api/queryClient';
import { QueryKey } from '@/Config/api/queryKey';
import i18n from '@/Config/app/i18n';
import { ActionDto } from '@/Domain/api/Action/dto/Action.dto';
import {
  ConnectRemoteMcpDto,
  RemoteMcpConnectResult,
  RemoteMcpCreateDto,
  RemoteMcpDto,
  RemoteMcpTool,
  RemoteMcpUpdateDto,
  isRemoteMcpOAuthRequired,
} from '@/Domain/api/Action/dto/RemoteMCP.dto';
import {
  PostHogEventEnum,
  type RemoteMcpConnectFailReason,
  type RemoteMcpEntryPoint,
} from '@/Domain/enum/PostHogEvent.enum';
import { useRemoteMcpOAuth } from '@/Domain/hooks/Action/RemoteMcp/useRemoteMcpOAuth.hook';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { useModalStore } from '@/Domain/store/modal.store';

const isSubset = (setA: Set<string>, setB: Set<string>): boolean => {
  let result = true;
  setA.forEach((val) => {
    if (!setB.has(val)) {
      result = false;
    }
  });
  return result;
};

const getDefaultTools = (toolNames: string[]): RemoteMcpTool[] =>
  toolNames.map((toolName) => ({
    name: toolName,
    is_active: true,
  }));

const updateToolNamesOnReconnect = (
  toolNames: string[],
  remoteMcpAction: RemoteMcpDto,
): RemoteMcpTool[] =>
  toolNames.map((toolName) => {
    const tool = remoteMcpAction.allowed_tools.find((tool) => tool.name === toolName);
    return {
      name: toolName,
      is_active: tool ? tool.is_active : remoteMcpAction.default_tool_is_active,
      thinking_message: tool?.thinking_message ?? null,
      // Preserve captures without changing untouched tool payloads.
      ...(tool?.save_to_memory?.length ? { save_to_memory: tool.save_to_memory } : {}),
    };
  });

const connectRemoteMcpActionSchema = z.object({
  remote_mcp_url: z.string().url().min(1),
  headers: z
    .array(
      z.object({
        id: z.number().or(z.string()),
        name: z.string().max(ACTION_LIMITS.BODY_KEY, {
          message: i18n.t('actions_page:sheet.form.validation.key_too_long', {
            limit: ACTION_LIMITS.BODY_KEY,
          }),
        }),
        value: z.string().max(ACTION_LIMITS.BODY_VALUE, {
          message: i18n.t('actions_page:sheet.form.validation.value_too_long', {
            limit: ACTION_LIMITS.BODY_VALUE,
          }),
        }),
      }),
    )
    .refine((items) => {
      const names = items.map((item) => item.name.trim());
      return new Set(names).size === names.length;
    }),
  // Optional metadata a catalog pick fills so the created action carries the service's name,
  // brand icon and description. Empty for a manual URL entry (the backend applies defaults).
  name: z.string().optional(),
  description: z.string().optional(),
  icon: z.string().optional(),
  icon_color: z.string().optional(),
});

export type ConnectRemoteMcpForm = z.infer<typeof connectRemoteMcpActionSchema>;

// Only the HTTP status is knowable here; the backend owns the precise classification.
const connectFailReason = (
  error: unknown,
  stage: 'probe' | 'create',
): RemoteMcpConnectFailReason => {
  const status = (error as AxiosError)?.response?.status;
  if (status === undefined || status >= 500) return 'backend_error';
  return stage === 'probe' ? 'probe_rejected' : 'create_failed';
};

export const useConnectRemoteMcpActionForm = (remoteMcpAction?: RemoteMcpDto) => {
  const { t } = useTranslation('remote_mcp');
  const { capture } = usePostHogAnalytics();
  // Set by the caller right before submitting, so the funnel can tell a catalog tile from a
  // hand-typed URL from a reconnect on an existing action.
  const entryPointRef = useRef<RemoteMcpEntryPoint>('manual');
  // A deep link probes without a click, so it withholds persistence until the user asks for it.
  const persistRef = useRef(true);
  const submissionPendingRef = useRef(false);
  const [needsManualConfirm, setNeedsManualConfirm] = useState(false);

  const { setParams } = useModalStore();
  const form = useForm<ConnectRemoteMcpForm>({
    mode: 'onChange',
    defaultValues: {
      remote_mcp_url: remoteMcpAction?.remote_mcp_url || '',
      headers: remoteMcpAction?.headers || [],
      name: '',
      description: '',
      icon: '',
      icon_color: '',
    },
    resolver: zodResolver(connectRemoteMcpActionSchema),
  });

  const connectRemoteMcpMutation = useMutation<
    RemoteMcpConnectResult,
    unknown,
    ConnectRemoteMcpDto
  >({
    mutationKey: [QueryKey.actions.remoteMcp.connect],
  });

  const oauth = useRemoteMcpOAuth({
    remoteMcpAction,
    getConnectFormData: () => form.getValues(),
  });
  const createRemoteMcpMutation = useMutation<RemoteMcpDto, unknown, RemoteMcpCreateDto>({
    mutationKey: [QueryKey.actions.remoteMcp.create],
  });
  const activateActionMutation = useMutation<
    ActionDto,
    unknown,
    { id: number; is_active: boolean }
  >({
    mutationKey: [QueryKey.actions.basic.patch],
  });

  const defaultPatchRemoteMcpMutationOptions = queryClient.getMutationDefaults([
    QueryKey.actions.remoteMcp.update,
  ]);
  const patchRemoteMcpMutation = useMutation<RemoteMcpDto, unknown, RemoteMcpUpdateDto>({
    mutationKey: [QueryKey.actions.remoteMcp.update, remoteMcpAction?.id],
    ...defaultPatchRemoteMcpMutationOptions,
    onSuccess: (data, variables, onMutateResult, context) => {
      defaultPatchRemoteMcpMutationOptions?.onSuccess?.(data, variables, onMutateResult, context);

      const dbToolNames = new Set(remoteMcpAction?.allowed_tools.map((tool) => tool.name));
      const responseToolNames = new Set(data.allowed_tools.map((tool) => tool.name));

      if (!isSubset(dbToolNames, responseToolNames)) toast.info(t('toast.lack_of_tool_detected'));
      if (!isSubset(responseToolNames, dbToolNames)) toast.info(t('toast.new_tools_detected'));
    },
  });
  const isActionUpdatePending =
    useIsMutating({
      mutationKey: [QueryKey.actions.remoteMcp.update, remoteMcpAction?.id],
    }) > 0;
  const isSubmissionPending = (): boolean =>
    submissionPendingRef.current ||
    queryClient.isMutating({ mutationKey: [QueryKey.actions.remoteMcp.connect] }) > 0 ||
    queryClient.isMutating({ mutationKey: [QueryKey.actions.remoteMcp.create] }) > 0 ||
    activateActionMutation.isPending ||
    (!!remoteMcpAction &&
      queryClient.isMutating({
        mutationKey: [QueryKey.actions.remoteMcp.update, remoteMcpAction.id],
      }) > 0);

  const handleSubmit = async (data: ConnectRemoteMcpForm): Promise<void> => {
    if (
      remoteMcpAction &&
      queryClient.isMutating({
        mutationKey: [QueryKey.actions.remoteMcp.update, remoteMcpAction.id],
      }) > 0
    )
      return;

    const server = remoteMcpServerLabel(data.remote_mcp_url);
    const isReconnect = !!remoteMcpAction;
    capture(PostHogEventEnum.RemoteMcpConnectStarted, {
      server,
      entry_point: entryPointRef.current,
      is_reconnect: isReconnect,
    });

    let toolNames: string[];
    try {
      const result = await connectRemoteMcpMutation.mutateAsync(data);
      if (isRemoteMcpOAuthRequired(result)) {
        capture(PostHogEventEnum.RemoteMcpConnectRequiresOauth, {
          server,
          provider_label: result.provider_label || '',
        });
        oauth.beginConsent(result);
        return;
      }
      toolNames = result.tool_names;
      if (!remoteMcpAction && !persistRef.current) {
        // Probe only: creating and activating here would need no click of the user's.
        capture(PostHogEventEnum.RemoteMcpConnectAwaitingConfirmation, {
          server,
          tool_count: result.tool_names.length,
        });
        setNeedsManualConfirm(true);
        return;
      }
    } catch (error) {
      capture(PostHogEventEnum.RemoteMcpConnectFailed, {
        server,
        reason: connectFailReason(error, 'probe'),
      });
      // Keep the chosen server in front of the user so a failed auto-probe can be retried.
      if (!remoteMcpAction && !persistRef.current) setNeedsManualConfirm(true);
      return;
    }

    let responseRemoteMcpAction: RemoteMcpDto;
    if (!remoteMcpAction) {
      try {
        responseRemoteMcpAction = await createRemoteMcpMutation.mutateAsync({
          ...data,
          allowed_tools: getDefaultTools(toolNames),
        });
        // Open (no-auth) server connected: auto-enable so the user doesn't have to flip the toggle
        // (OAuth actions auto-enable on finalize; keep the two paths consistent).
        try {
          const activated = await activateActionMutation.mutateAsync({
            id: responseRemoteMcpAction.id,
            is_active: true,
          });
          responseRemoteMcpAction = {
            ...responseRemoteMcpAction,
            is_active: activated.is_active,
          };
        } catch {
          /* leave it off if activation is blocked (e.g. action limit); the user can toggle it */
        }
      } catch (error) {
        capture(PostHogEventEnum.RemoteMcpConnectFailed, {
          server,
          reason: connectFailReason(error, 'create'),
        });
        return;
      }
    } else {
      if (
        queryClient.isMutating({
          mutationKey: [QueryKey.actions.remoteMcp.update, remoteMcpAction.id],
        }) > 0
      )
        return;
      try {
        responseRemoteMcpAction = await patchRemoteMcpMutation.mutateAsync({
          id: remoteMcpAction.id,
          remote_mcp_url: data.remote_mcp_url,
          headers: data.headers,
          allowed_tools: updateToolNamesOnReconnect(toolNames, remoteMcpAction),
          ...(remoteMcpAction.oauth ? { clear_oauth_credentials: true } : {}),
        });
      } catch (error) {
        capture(PostHogEventEnum.RemoteMcpConnectFailed, {
          server,
          reason: connectFailReason(error, 'create'),
        });
        return;
      }
    }
    // Emitted only once the action actually exists, so the funnel identity documented on the enum
    // (started = succeeded + requires_oauth + failed) holds: a probe that succeeds but whose create
    // is then blocked by the action limit must count as failed, not as both.
    capture(PostHogEventEnum.RemoteMcpConnectSucceeded, {
      server,
      tool_count: toolNames.length,
      is_reconnect: isReconnect,
    });
    setParams({ remoteMcpAction: responseRemoteMcpAction });
  };

  const submitFrom = (entryPoint: RemoteMcpEntryPoint, { persist = true } = {}) => {
    if (isSubmissionPending()) return;
    entryPointRef.current = entryPoint;
    persistRef.current = persist;
    setNeedsManualConfirm(false);
    submissionPendingRef.current = true;
    const submission = form.handleSubmit(handleSubmit)();
    void submission.finally(() => {
      submissionPendingRef.current = false;
    });
    return submission;
  };

  return {
    form,
    handleSubmit,
    submitFrom,
    needsManualConfirm,
    oauth,
    isPending:
      connectRemoteMcpMutation.isPending ||
      createRemoteMcpMutation.isPending ||
      activateActionMutation.isPending ||
      patchRemoteMcpMutation.isPending ||
      isActionUpdatePending,
    isSubmissionPending,
  };
};
